import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { Box, Chip, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { AttachmentStrip } from './AttachmentStrip';
import type { EpiRunState, EpiStagedAttachment } from '../types/epi-chat-types';
import {
  ATTACHMENT_ACCEPT_ATTR,
  attachmentGate,
  attachTooltip,
  canSendMessage,
} from '../utils/attachmentLimits';

/** How long the loading tooltip stays up after an Enter-press that was
 *  blocked because the session is still loading. */
const LOADING_TIP_FLASH_MS = 2500;

/**
 * Lightweight descriptor of the current scope, shown as a chip above the
 * input. `null` = no chip (project scope / root). The composer is
 * intentionally agnostic to the full Scope discriminated union — the
 * parent resolves the chip's label and clear behavior.
 */
export interface ChatComposerScopeChip {
  /** Prefix label, e.g. "Cohort" or "Variable". */
  kindLabel: string;
  /** Artifact name. `undefined` while the lookup is in flight (renders skeleton). */
  name: string | undefined;
  /**
   * Optional dismiss callback. When omitted, the chip renders as
   * static (no X). OR-6021: the agent's task scope is runtime state
   * — the user does not get to clear it, so callers driving the chip
   * from ``task_scope`` events leave this undefined.
   */
  onClear?: () => void;
}

interface ChatComposerProps {
  runState: EpiRunState;
  /**
   * OR-6348: renders the Stop-button/streaming state even when ``runState``
   * itself is still 'idle' — covers a brand-new chat's first send, where
   * there's no real run yet to key off (chat row creation + subscribe are
   * still in flight). Purely visual: ``onCancel`` still fires as normal if
   * clicked, but there's no real run to cancel yet in that window.
   */
  forceStreaming?: boolean;
  onSend: (text: string) => void;
  onCancel: () => void;
  /**
   * OR-6125: file-upload entry point. When provided, the attach button is enabled
   * and clicking it opens a file picker; the chosen file is handed back here (the
   * parent POSTs it to the upload endpoint). Omitted (e.g. flag off) → the button
   * stays disabled.
   */
  onAttach?: (file: File) => void;
  /**
   * Alternative to {@link onAttach}: when provided, the attach button opens
   * this handler directly (e.g. the "Create concept group" modal) instead of a
   * native file picker. Takes precedence over ``onAttach`` when both are set.
   */
  onAttachClick?: () => void;
  /**
   * Files staged for the next message, rendered as cards above the input. They
   * carry their own upload status; Send is held while any is still uploading.
   */
  attachments?: EpiStagedAttachment[];
  /** Drops one staged attachment. Required for the cards to render a remove control. */
  onRemoveAttachment?: (id: string) => void;
  /** Accepted file extensions for the attach picker (e.g. ".csv,.xlsx,.xls"). */
  attachAccept?: string;
  placeholder?: string;
  /**
   * Hard-disable the composer — blocks typing and every control. Reserved for
   * "not connected": there is nothing the user can usefully do, so the input
   * goes inert. Session-loading is NOT a hard-disable; see ``loadingMessage``.
   */
  disabled?: boolean;
  /**
   * Soft "still loading" gate (OR-6139 AC 2). When set, the user may type but
   * cannot submit yet: the Send button is disabled and this string renders as
   * its tooltip, and pressing Enter flashes the same tooltip instead of
   * sending. ``null``/``undefined`` means the session is ready to accept a
   * message. Ignored while a run is streaming (the Stop button owns that state).
   */
  loadingMessage?: string | null;
  /**
   * Current scope shown as a chip above the input. When omitted (project
   * scope), no chip is rendered. The chip is the only scope indicator in
   * the UI; messages are not scope-badged.
   */
  scopeChip?: ChatComposerScopeChip | null;
  /**
   * Optional trailing-controls slot rendered below the input. Used by the
   * legacy ``/epi-study-agent`` page to host the dataset-pin chip.
   * Project-bound chats omit this slot — the project owns the dataset.
   */
  trailingControl?: ReactNode;
}

export const ChatComposer = ({
  runState,
  forceStreaming,
  onSend,
  onCancel,
  onAttach,
  onAttachClick,
  attachments,
  onRemoveAttachment,
  attachAccept = ATTACHMENT_ACCEPT_ATTR,
  placeholder = 'Send a message...',
  disabled,
  loadingMessage,
  scopeChip,
  trailingControl,
}: ChatComposerProps) => {
  const [value, setValue] = useState('');
  const streaming = runState === 'streaming' || Boolean(forceStreaming);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFilePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again re-fires onChange.
    event.target.value = '';
    if (file) {
      onAttach?.(file);
    }
  };
  // A run owns the in-flight (Stop) state, so the loading gate only applies
  // when no run is streaming. Typing stays allowed while loading; only submit
  // is held back.
  const loading = !streaming && Boolean(loadingMessage);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // Controlled tooltip on the Send button so an Enter-press can surface "still
  // loading" even though the button itself is disabled and never focused.
  const [loadingTipOpen, setLoadingTipOpen] = useState(false);
  const tipTimerRef = useRef<number | null>(null);

  // Allow focus as soon as the input is typeable (connected), including while
  // the session is still loading, so the user can start composing immediately.
  const canType = !disabled;
  useEffect(() => {
    if (canType && !streaming) {
      inputRef.current?.focus();
    }
  }, [canType, streaming]);

  useEffect(
    () => () => {
      if (tipTimerRef.current !== null) window.clearTimeout(tipTimerRef.current);
    },
    []
  );

  const flashLoadingTip = () => {
    setLoadingTipOpen(true);
    if (tipTimerRef.current !== null) window.clearTimeout(tipTimerRef.current);
    tipTimerRef.current = window.setTimeout(() => setLoadingTipOpen(false), LOADING_TIP_FLASH_MS);
  };

  const { uploading: uploadingAttachment, atLimit } = attachmentGate(attachments);
  const canSend = canSendMessage({ text: value, attachments });

  const handleSend = () => {
    if (disabled || streaming || loading) return;
    if (!canSend) return;
    onSend(value.trim());
    setValue('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (streaming || uploadingAttachment) return;
      if (loading) {
        // Submit is held until the session finishes loading — tell the user why
        // rather than silently swallowing their Enter.
        flashLoadingTip();
        return;
      }
      handleSend();
    }
  };

  return (
    <Stack spacing={0.75}>
      {scopeChip ? (
        <Stack direction="row" sx={{ paddingX: 0.5 }}>
          <Chip
            size="small"
            data-testid="composer-scope-chip"
            label={
              // OR-6021: render the kind label alone when name is
              // absent — no skeleton, no trailing colon. The chip
              // signals what the agent is working on; if the name
              // isn't known yet, showing a skeleton is noisier than
              // helpful (and caused visible flicker when the scope
              // transitioned between draft and persisted variants).
              scopeChip.name === undefined ? (
                <Typography variant="caption">
                  <strong>{scopeChip.kindLabel}</strong>
                </Typography>
              ) : (
                <Typography variant="caption">
                  <strong>{scopeChip.kindLabel}:</strong> {scopeChip.name}
                </Typography>
              )
            }
            onDelete={scopeChip.onClear}
            deleteIcon={
              <Tooltip title="Clear scope (return to project)">
                <CloseIcon data-testid="composer-scope-chip-clear" />
              </Tooltip>
            }
            sx={{ maxWidth: '100%' }}
          />
        </Stack>
      ) : null}
      <Paper
        elevation={0}
        sx={{
          // Column so staged attachments sit inside the input's own box.
          display: 'flex',
          flexDirection: 'column',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <AttachmentStrip
          attachments={attachments ?? []}
          onRemove={onRemoveAttachment}
          testId="composer-attachments"
          cardTestIdPrefix="composer-attachment-"
        />
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Tooltip
            title={attachTooltip({
              atLimit,
              canAttach: Boolean(onAttachClick || onAttach),
              accept: attachAccept,
            })}
          >
            <span style={{ display: 'flex', alignSelf: 'center', marginLeft: 8 }}>
              <IconButton
                size="small"
                // onAttachClick opens the create-concept-group modal directly;
                // onAttach falls back to the native file picker.
                onClick={onAttachClick ?? (() => fileInputRef.current?.click())}
                disabled={(!onAttach && !onAttachClick) || disabled || atLimit}
                aria-label="Attach a file"
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {!onAttachClick && (
            <input
              ref={fileInputRef}
              type="file"
              accept={attachAccept}
              hidden
              onChange={handleFilePicked}
              data-testid="composer-file-input"
            />
          )}
          <TextField
            fullWidth
            multiline
            maxRows={6}
            variant="standard"
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            data-testid="epi-composer-input"
            inputRef={inputRef}
            InputProps={{ disableUnderline: true }}
            sx={{
              flex: 1,
              paddingX: 1.5,
              display: 'flex',
              '& .MuiInputBase-root': { alignItems: 'center', minHeight: 44 },
            }}
          />
          {streaming ? (
            <Tooltip title="Stop generating">
              <IconButton
                size="small"
                color="error"
                onClick={onCancel}
                aria-label="Cancel the in-flight agent run"
                sx={{ alignSelf: 'center', marginRight: 1 }}
              >
                <StopCircleIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              title={loading ? loadingMessage : 'Send (Enter)'}
              // Controlled only while loading, so an Enter-press can pop it; the
              // ready state falls back to MUI's default hover behaviour.
              open={loading ? loadingTipOpen : undefined}
              onOpen={loading ? () => setLoadingTipOpen(true) : undefined}
              onClose={loading ? () => setLoadingTipOpen(false) : undefined}
            >
              <span style={{ display: 'flex' }}>
                <IconButton
                  onClick={handleSend}
                  disabled={disabled || loading || !canSend}
                  aria-label="Send message"
                  data-testid="epi-composer-send"
                  sx={{
                    alignSelf: 'stretch',
                    width: 48,
                    borderRadius: 0,
                    // Stays visually identical (filled blue) even while
                    // disabled (no text yet) — matches spec; the `disabled`
                    // attribute still blocks the click regardless of styling.
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    '&.Mui-disabled': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                    },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Paper>
      {trailingControl !== undefined && trailingControl !== null && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{ paddingX: 0.5, justifyContent: 'flex-end' }}
        >
          {trailingControl}
        </Stack>
      )}
    </Stack>
  );
};
